import { createFileRoute, Link } from "@tanstack/react-router";
import { LogoMark } from "@/components/RheaPrototypeHeader";
import { EngineSwitcher } from "@/components/EngineSwitcher";
import { motion, useReducedMotion } from "framer-motion";

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/coming-soon/$vertical")({
  component: ComingSoonPage,
  head: ({ params }) => ({
    meta: [
      { title: `${verticalMeta(params.vertical).title} — Sift Trip` },
      { name: "description", content: "Estamos construyendo esta experiencia para ti." },
    ],
  }),
});

// ─── Design tokens (matches index.tsx) ───────────────────────────────────────

const T = {
  navy:        "oklch(0.120 0.040 258)",
  navyMid:     "oklch(0.170 0.055 255)",
  blue:        "oklch(0.580 0.220 256)",
  white:       "oklch(1 0 0)",
  cream:       "oklch(0.988 0.003 240)",
  parchment:   "oklch(0.993 0.004 72)",
  ink:         "oklch(0.07 0.012 258)",
  inkMuted:    "oklch(0.38 0.016 258 / 0.52)",
  border:      "oklch(0.880 0.014 70 / 0.50)",
  borderSoft:  "oklch(0.880 0.014 70 / 0.28)",
} as const;

const EASE = [0.22, 1, 0.36, 1] as const;

// ─── Vertical metadata ────────────────────────────────────────────────────────

interface VerticalMeta {
  title:       string;
  tagline:     string;
  description: string;
  accent:      string;
}

function verticalMeta(vertical: string): VerticalMeta {
  const MAP: Record<string, VerticalMeta> = {
    alojamiento: {
      title:       "Alojamiento",
      tagline:     "Tu refugio en cada ciudad",
      description: "Hoteles, apartamentos y alojamientos únicos curados para tu estilo de viaje. La manera más inteligente de encontrar dónde quedarte.",
      accent:      "oklch(0.580 0.220 256)",
    },
    vuelos: {
      title:       "Vuelos",
      tagline:     "La ruta que tiene sentido",
      description: "Combinamos precio, escalas y horario para encontrar el vuelo que conviene para tu ruta — no solo el más barato.",
      accent:      "oklch(0.52 0.18 278)",
    },
    trenes: {
      title:       "Trenes",
      tagline:     "Viaja entre ciudades con contexto",
      description: "Rutas ferroviarias inteligentes para moverte por Europa y América del Sur con comodidad y perspectiva local.",
      accent:      "oklch(0.50 0.16 240)",
    },
    planner: {
      title:       "Planner",
      tagline:     "Tu viaje completo, día a día",
      description: "El itinerario completo — vuelos, trenes, experiencias y alojamiento unidos en una ruta coherente. Para cuando quieras verlo todo junto.",
      accent:      "oklch(0.716 0.082 74)",
    },
  };
  return MAP[vertical] ?? {
    title:       vertical.charAt(0).toUpperCase() + vertical.slice(1),
    tagline:     "Próximamente",
    description: "Estamos construyendo esta experiencia para ti.",
    accent:      "oklch(0.580 0.220 256)",
  };
}

// ─── Abstract Editorial Illustration ─────────────────────────────────────────
// Compass rose + constellation route — editorial / cartographic feel.
// No external assets; pure SVG, Framer Motion for subtle float.

function EditorialIllustration({ accent }: { accent: string }) {
  const reduce   = useReducedMotion();
  const ink      = T.navy;
  const inkMid   = "oklch(0.280 0.025 258)";
  const inkFaint = "oklch(0.380 0.020 258 / 0.25)";

  return (
    <div className="relative select-none" aria-hidden style={{ width: "300px", height: "300px" }}>

      {/* ── Outer ring — dotted ─────────────────────────────────────────── */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 300 300"
        fill="none"
        overflow="visible"
      >
        {/* Outer dotted circle */}
        <circle cx="150" cy="150" r="130"
          stroke={inkFaint} strokeWidth="1"
          strokeDasharray="3 7" strokeLinecap="round" />

        {/* Mid grid ring */}
        <circle cx="150" cy="150" r="90"
          stroke={inkFaint} strokeWidth="0.7"
          strokeDasharray="2 10" strokeLinecap="round" />

        {/* Inner ring */}
        <circle cx="150" cy="150" r="50"
          stroke={inkFaint} strokeWidth="0.7" />

        {/* Cardinal cross lines */}
        <path d="M150 20 L150 280" stroke={inkFaint} strokeWidth="0.7" strokeDasharray="2 8" />
        <path d="M20 150 L280 150" stroke={inkFaint} strokeWidth="0.7" strokeDasharray="2 8" />

        {/* Diagonal lines at 45° */}
        <path d="M58 58 L242 242" stroke={inkFaint} strokeWidth="0.5" strokeDasharray="1 12" />
        <path d="M242 58 L58 242" stroke={inkFaint} strokeWidth="0.5" strokeDasharray="1 12" />

        {/* Constellation route — draws itself on mount */}
        <motion.path
          d="M 85 88  C 100 70 128 72 148 80  C 168 88 192 78 210 92
             C 228 106 230 128 218 148 C 206 168 215 190 200 208
             C 185 226 162 232 148 222"
          stroke={accent}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeOpacity="0.60"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={
            reduce
              ? { duration: 0 }
              : {
                  pathLength: { duration: 2.0, ease: "easeInOut" as const, delay: 0.4 },
                  opacity:    { duration: 0.3, delay: 0.4 },
                }
          }
        />
      </svg>

      {/* ── Compass rose (gently rotates) ───────────────────────────────── */}
      <motion.div
        className="absolute"
        style={{ top: "75px", left: "75px", width: "150px", height: "150px" }}
        animate={reduce ? undefined : { rotate: [0, 360] }}
        transition={reduce ? undefined : { duration: 60, repeat: Infinity, ease: "linear" as const }}
      >
        <svg viewBox="0 0 150 150" fill="none">
          {/* N / S arrows */}
          <path d="M75 12 L82 42 L75 38 L68 42 Z" fill={ink} fillOpacity="0.90" />
          <path d="M75 138 L68 108 L75 112 L82 108 Z" fill={inkMid} fillOpacity="0.45" />
          {/* E / W arrows */}
          <path d="M138 75 L108 82 L112 75 L108 68 Z" fill={inkMid} fillOpacity="0.45" />
          <path d="M12 75 L42 68 L38 75 L42 82 Z" fill={inkMid} fillOpacity="0.45" />
          {/* Diagonal spurs */}
          {[45, 135, 225, 315].map(deg => {
            const r = Math.PI * deg / 180;
            const x1 = 75 + Math.cos(r) * 52;
            const y1 = 75 + Math.sin(r) * 52;
            const x2 = 75 + Math.cos(r) * 36;
            const y2 = 75 + Math.sin(r) * 36;
            return (
              <path key={deg}
                d={`M${x1} ${y1} L${x2} ${y2}`}
                stroke={inkMid} strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.40" />
            );
          })}
          {/* Inner ring */}
          <circle cx="75" cy="75" r="22" stroke={inkMid} strokeWidth="0.8" strokeOpacity="0.30" />
          {/* Centre jewel */}
          <circle cx="75" cy="75" r="7" fill={accent} fillOpacity="0.80" />
          <circle cx="75" cy="75" r="3.5" fill={T.cream} />
        </svg>
      </motion.div>

      {/* ── City dots on constellation ───────────────────────────────────── */}
      {[
        { cx: 85,  cy: 88  },
        { cx: 148, cy: 80  },
        { cx: 210, cy: 92  },
        { cx: 218, cy: 148 },
        { cx: 200, cy: 208 },
        { cx: 148, cy: 222 },
      ].map(({ cx, cy }, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: `${cx - 5}px`, top: `${cy - 5}px`, width: "10px", height: "10px" }}
          animate={reduce ? undefined : { scale: [1, 1.25, 1], opacity: [0.7, 1, 0.7] }}
          transition={reduce ? undefined : { duration: 2.5, repeat: Infinity, ease: "easeInOut" as const, delay: i * 0.4 }}
        >
          <svg viewBox="0 0 10 10" fill="none">
            <circle cx="5" cy="5" r="3" fill={accent} fillOpacity="0.75" />
            <circle cx="5" cy="5" r="1.5" fill={T.parchment} />
          </svg>
        </motion.div>
      ))}

      {/* ── Floating "blueprint" fragment (top-right) ────────────────────── */}
      <motion.div
        className="absolute"
        style={{ top: "24px", right: "18px" }}
        animate={reduce ? undefined : { y: [0, -6, 0] }}
        transition={reduce ? undefined : { duration: 4.5, repeat: Infinity, ease: "easeInOut" as const }}
      >
        <svg width="56" height="44" viewBox="0 0 56 44" fill="none">
          <rect x="0.5" y="0.5" width="55" height="43" rx="3"
            fill="oklch(0.580 0.220 256 / 0.06)"
            stroke={accent} strokeWidth="0.8" strokeOpacity="0.35" />
          {/* Grid lines */}
          <path d="M0 10 L56 10M0 22 L56 22M0 34 L56 34" stroke={accent} strokeWidth="0.5" strokeOpacity="0.20" />
          <path d="M14 0 L14 44M28 0 L28 44M42 0 L42 44" stroke={accent} strokeWidth="0.5" strokeOpacity="0.20" />
          {/* Route sketch */}
          <path d="M8 36 C14 26 20 18 28 14 C36 10 44 12 50 20"
            stroke={accent} strokeWidth="1.2" strokeLinecap="round" fill="none" strokeOpacity="0.60" />
          {/* Pin */}
          <circle cx="28" cy="14" r="2.5" fill={accent} fillOpacity="0.70" />
        </svg>
      </motion.div>

      {/* ── Floating "ticket" fragment (bottom-left) ─────────────────────── */}
      <motion.div
        className="absolute"
        style={{ bottom: "22px", left: "16px" }}
        animate={reduce ? undefined : { y: [0, -4, 0] }}
        transition={reduce ? undefined : { duration: 5.2, repeat: Infinity, ease: "easeInOut" as const, delay: 0.6 }}
      >
        <svg width="62" height="32" viewBox="0 0 62 32" fill="none">
          <rect x="0.5" y="0.5" width="61" height="31" rx="5"
            fill={T.parchment}
            stroke={inkMid} strokeWidth="0.8" strokeOpacity="0.25" />
          {/* Perforation */}
          <path d="M20 0.5 L20 31.5" stroke={inkFaint} strokeWidth="0.6" strokeDasharray="2 3" />
          {/* Barcode lines */}
          {[26, 29, 32, 35, 38, 40, 43, 46].map(x => (
            <path key={x} d={`M${x} 6 L${x} 26`}
              stroke={inkMid} strokeWidth={x % 3 === 0 ? "1.2" : "0.6"} strokeOpacity="0.35" />
          ))}
          {/* Star icon on stub */}
          <path d="M10 16 L11.2 13 L12.4 16 L15 16 L13 17.8 L13.8 21 L10 19 L6.2 21 L7 17.8 L5 16 L7.6 16 Z"
            fill={accent} fillOpacity="0.70" />
        </svg>
      </motion.div>
    </div>
  );
}

// ─── Mini top bar (no full Navbar — __root.tsx suppresses it for /coming-soon) ──

function MiniHeader() {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center px-5 sm:px-8"
      style={{
        background:          "oklch(0.994 0.006 70 / 0.94)",
        backdropFilter:      "blur(18px)",
        WebkitBackdropFilter:"blur(18px)",
        borderBottom:        `1px solid ${T.borderSoft}`,
      }}
    >
      <Link to="/" aria-label="Sift Trip — inicio">
        <LogoMark />
      </Link>

      <EngineSwitcher />

      <div className="flex-1" />
    </header>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ComingSoonPage() {
  const { vertical } = Route.useParams();
  const meta   = verticalMeta(vertical);
  const reduce = useReducedMotion();

  const fadeUp = (delay = 0) => ({
    initial:    reduce ? false : { opacity: 0, y: 18 },
    animate:    { opacity: 1, y: 0 },
    transition: reduce ? { duration: 0 } : { duration: 0.55, delay, ease: EASE },
  });

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-x-hidden"
      style={{ background: T.cream }}
    >
      <MiniHeader />

      {/* Ambient glow */}
      <div
        className="fixed inset-0 pointer-events-none"
        aria-hidden
        style={{
          background: `radial-gradient(ellipse 70% 50% at 65% 25%, ${meta.accent} / 0.07 0%, transparent 65%)`,
        }}
      />

      {/* Background constellation — scale on INNER element so overflow-hidden clips correctly */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
        aria-hidden
        style={{ opacity: 0.13 }}
      >
        <div style={{ transform: "scale(1.5)", transformOrigin: "center" }}>
          <EditorialIllustration accent={meta.accent} />
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-24 pb-16 relative z-10">
        <div className="max-w-md w-full text-center">

          {/* Editorial photograph */}
          <motion.div
            {...fadeUp(0)}
            className="flex justify-center mb-8"
          >
            <img
              src="/coming-soon-greek-artisans.webp"
              alt="Vista editorial"
              className="w-full rounded-2xl object-cover"
              style={{
                maxWidth:  "300px",
                maxHeight: "220px",
                objectPosition: "center",
                boxShadow: "0 10px 40px oklch(0.07 0.012 258 / 0.13), 0 2px 10px oklch(0.07 0.012 258 / 0.07)",
                border:    `1px solid ${T.borderSoft}`,
              }}
            />
          </motion.div>

          {/* Badge */}
          <motion.div {...fadeUp(0.10)} className="mb-4">
            <span
              className="inline-block text-[10px] font-bold tracking-[0.20em] uppercase px-3 py-1 rounded-full"
              style={{
                color:      meta.accent,
                background: `color-mix(in oklch, ${meta.accent} 10%, transparent)`,
                border:     `1px solid color-mix(in oklch, ${meta.accent} 28%, transparent)`,
              }}
            >
              Próximamente
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            {...fadeUp(0.17)}
            className="font-sans font-extralight leading-none mb-2"
            style={{
              fontSize:      "clamp(2.8rem, 8vw, 4.5rem)",
              letterSpacing: "-0.035em",
              color:         T.navy,
            }}
          >
            {meta.title}
          </motion.h1>

          {/* Tagline */}
          <motion.p
            {...fadeUp(0.22)}
            className="text-base font-medium mb-4"
            style={{ color: meta.accent }}
          >
            {meta.tagline}
          </motion.p>

          {/* Description */}
          <motion.p
            {...fadeUp(0.28)}
            className="text-sm leading-relaxed mb-10 max-w-xs mx-auto"
            style={{ color: T.inkMuted }}
          >
            {meta.description}
          </motion.p>

          <motion.div {...fadeUp(0.36)}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-200"
              style={{
                background: T.navy,
                color:      T.parchment,
                boxShadow:  `0 4px 20px oklch(0.120 0.040 258 / 0.22)`,
              }}
              onMouseEnter={e => {
                if (reduce) return;
                (e.currentTarget as HTMLElement).style.transform  = "translateY(-2px)";
                (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 28px oklch(0.120 0.040 258 / 0.32)";
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.transform  = "";
                (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 20px oklch(0.120 0.040 258 / 0.22)";
              }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
                <path d="M10 6.5H3M6 3.5l-3 3 3 3"
                  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Volver a Experiencias
            </Link>
          </motion.div>

          {/* Wordmark */}
          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={reduce ? { duration: 0 } : { duration: 0.5, delay: 0.55 }}
            className="mt-14 text-xs"
            style={{ color: "oklch(0.120 0.040 258 / 0.20)" }}
          >
            Sift Trip · inteligencia de viaje
          </motion.p>
        </div>
      </div>
    </div>
  );
}